'use client';

import { type ProductFilterSchema, productFilterSchema } from '@coas/validators/payload';
import { atom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';

const toForm = (f: ProductFilterSchema): ProductFilterSchema => ({
    ...f,
    brands: f.brands ?? [],
    colors: f.colors ?? [],
    sizes: f.sizes ?? [],
});

const toCanonical = (f: ProductFilterSchema): ProductFilterSchema =>
    productFilterSchema.parse({
        ...f,
        brands: Array.isArray(f.brands) && f.brands.length ? f.brands : null,
        colors: Array.isArray(f.colors) && f.colors.length ? f.colors : null,
        sizes: Array.isArray(f.sizes) && f.sizes.length ? f.sizes : null,
    });

const storage = createJSONStorage<ProductFilterSchema>(() => sessionStorage);

const canonicalFilterAtom = atomWithStorage<ProductFilterSchema>(
    'coas-product-filter',
    productFilterSchema.parse({}),
    storage,
);

const formFilterAtom = atom(
    get => toForm(get(canonicalFilterAtom)),
    (get, set, update: ProductFilterSchema | ((prev: ProductFilterSchema) => ProductFilterSchema)) => {
        const prevForm = toForm(get(canonicalFilterAtom));
        const nextForm = typeof update === 'function' ? update(prevForm) : update;
        const nextCanonical = toCanonical(nextForm);
        set(canonicalFilterAtom, nextCanonical);
    },
);

const uiAtom = atom<{ open: boolean; selected?: keyof ProductFilterSchema }>({
    open: false,
    selected: undefined,
});

export const filterStateAtom = atom(
    get => ({ ...get(uiAtom), filter: get(formFilterAtom) }),
    (
        get,
        set,
        update:
            | {
                  open?: boolean;
                  selected?: keyof ProductFilterSchema;
                  filter?: ProductFilterSchema;
              }
            | ((prev: { open: boolean; selected?: keyof ProductFilterSchema; filter: ProductFilterSchema }) => {
                  open?: boolean;
                  selected?: keyof ProductFilterSchema;
                  filter?: ProductFilterSchema;
              }),
    ) => {
        const prev = { ...get(uiAtom), filter: get(formFilterAtom) };
        const next = typeof update === 'function' ? update(prev) : { ...prev, ...update };

        set(uiAtom, { open: next.open ?? prev.open, selected: next.selected ?? prev.selected });

        if (next.filter) set(formFilterAtom, next.filter);

        return { ...get(uiAtom), filter: get(formFilterAtom) };
    },
);

export const apiFilterAtom = atom(get => get(canonicalFilterAtom));

export const isFilterAppliedAtom = atom(get => {
    const f = get(canonicalFilterAtom);
    return (
        (f.brands !== null && f.brands.length > 0) ||
        (f.colors !== null && f.colors.length > 0) ||
        (f.sizes !== null && f.sizes.length > 0) ||
        f.price[0] !== 0 ||
        f.price[1] !== 200_000
    );
});
